
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthGuard from '@/components/auth/AuthGuard';
import LoadingState from '@/components/football/LoadingState';
import FilterSection from '@/components/football/FilterSection';
import StatesList from '@/components/football/StatesList';
import AdminHeader from '@/components/layout/AdminHeader';
import AutomatedUpdatesManager from '@/components/admin/AutomatedUpdatesManager';
import { useAuth } from '@/contexts/AuthContext';
import { useFootballStates } from '@/hooks/useFootballStates';
import { useFootballFilters } from '@/hooks/useFootballFilters';
import { useStateHandlers } from '@/components/football/handlers/useStateHandlers';
import NavigationSection from '@/components/upload/NavigationSection';
import Header from '@/components/layout/Header';

const Football = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [mode, setMode] = useState<'latest' | 'custom'>('custom');

  const { savedStates, isLoading, refetch } = useFootballStates();
  
  useEffect(() => {
    // Check if we have state from navigation
    if (location.state) {
      const { mode, country, league, year } = location.state as any;
      
      if (mode === 'latest') {
        setMode('latest');
      } else if (mode === 'custom') {
        setMode('custom');
        setSelectedCountry(country || null);
        setSelectedLeague(league || null);
        setSelectedYear(year || null);
      }
      
      // Clear the state to prevent issues on refresh
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  
  const {
    availableCountries,
    availableLeagues,
    availableYears,
    filteredStates
  } = useFootballFilters(savedStates, selectedCountry, selectedLeague, selectedYear);

  const {
    handleLoadState,
    handleLoadLeagueTable,
    handleDeleteState
  } = useStateHandlers(refetch);

  // Function to filter and sort states to show newest results first
  const getFilteredAndSortedResults = () => {
    if (!savedStates) return [];
    
    let results = [...savedStates];
    
    // Filter by country and league if in custom mode
    if (mode === 'custom' && selectedCountry && selectedLeague) {
      results = results.filter(state => 
        state.country === selectedCountry && 
        state.league === selectedLeague
      );
    }
    
    // For latest mode, we want to show the most recent results
    // Assuming the data has Date field that can be used for sorting
    if (mode === 'latest') {
      // Get the most recent date across all matches
      const latestDates = new Set();
      results.forEach(state => {
        if (state.data && Array.isArray(state.data)) {
          state.data.forEach(match => {
            if (match.Date) {
              latestDates.add(match.Date);
            }
          });
        }
      });
      
      // Convert to array and sort to get most recent
      const sortedDates = Array.from(latestDates).sort().reverse();
      const mostRecentDate = sortedDates[0];
      
      // Filter results to only include matches from the most recent date
      results = results.map(state => {
        if (state.data && Array.isArray(state.data)) {
          const filteredData = state.data.filter(match => match.Date === mostRecentDate);
          return { ...state, data: filteredData };
        }
        return state;
      }).filter(state => state.data && state.data.length > 0);
    }
    
    // Sort by date (newest first)
    results.sort((a, b) => {
      const dateA = a.created_at || '';
      const dateB = b.created_at || '';
      return dateB.localeCompare(dateA);
    });
    
    return results;
  };

  const filteredAndSortedResults = getFilteredAndSortedResults();

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-8">
          <LoadingState />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container mx-auto py-8">
        <NavigationSection />
        <h1 className="text-3xl font-bold mb-6">
          {mode === 'latest' ? 'Latest Football Results' : 'Football Results'}
        </h1>
        
        {mode === 'custom' && (
          <FilterSection
            selectedCountry={selectedCountry}
            selectedLeague={selectedLeague}
            selectedYear={selectedYear}
            onCountrySelect={setSelectedCountry}
            onLeagueSelect={setSelectedLeague}
            onYearSelect={setSelectedYear}
            leagues={availableLeagues}
            years={availableYears}
          />
        )}

        <StatesList
          states={filteredAndSortedResults}
          onLoadState={handleLoadState}
          onLoadLeagueTable={handleLoadLeagueTable}
          onDeleteState={handleDeleteState}
        />

        {isAdmin && (
          <div className="mt-8">
            <AutomatedUpdatesManager />
          </div>
        )}
      </div>
    </>
  );
};

export default Football;
